import { useEffect, useMemo } from 'react';

import { ButtonBar } from 'components/ButtonBar/ButtonBar';
import { JobDetailsList } from 'components/DetailsList/Job/JobDetailsList';
import { PageToolbar } from 'components/PageToolbar/PageToolbar';
import { PrintHidden } from 'components/PrintHidden/PrintHidden';
import { useSetDocumentTitle } from 'hooks/useSetDocumentTitle';
import type { ApiJob } from 'modules/listing/api/job/types/ApiJob';
import { ListingViewActionButtons } from 'modules/listing/components/ListingView/ActionButtons/ListingViewActionButtons';
import { ListingViewAdminActionButtons } from 'modules/listing/components/ListingView/ActionButtons/ListingViewAdminActionButtons';
import { ListingViewBody } from 'modules/listing/components/ListingView/Body/ListingViewBody';
import { ListingViewFooter } from 'modules/listing/components/ListingView/Footer/ListingViewFooter';
import { ListingViewHeader } from 'modules/listing/components/ListingView/Header/ListingViewHeader';
import { ListingViewHeaderSearchContext } from 'modules/listing/components/ListingView/HeaderSearchContext/ListingViewHeaderSearchContext';
import { ListingViewMain } from 'modules/listing/components/ListingView/Main/ListingViewMain';
import { ListingViewSection } from 'modules/listing/components/ListingView/Section/ListingViewSection';
import { ListingViewSignupCta } from 'modules/listing/components/ListingView/SignupCta/ListingViewSignupCta';
import { ListingViewDescriptionSection } from 'modules/listing/components/ListingView/sections/Description/ListingViewDescriptionSection';
import { ListingViewApplySection } from 'modules/listing/components/ListingView/sections/ListingViewApplySection';
import { ListingViewBenefitsSection } from 'modules/listing/components/ListingView/sections/ListingViewBenefitsSection';
import { ListingViewLanguageProficiencySection } from 'modules/listing/components/ListingView/sections/ListingViewLanguageProficiencySection';
import { ListingViewLocationSection } from 'modules/listing/components/ListingView/sections/ListingViewLocationSection';
import { SearchContextHeader } from 'modules/search/components/Context/SearchContextHeader';
import { useSearchContextHeader } from 'modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { trackListing } from 'utils/analytics/trackListing';
import { getOrgName } from 'utils/listings/getOrgName';
import { isUserOrgOwner } from 'utils/listings/getOrgOwner';
import { isExternalURL } from 'utils/url/isExternalURL';
import { showModal } from 'zustand-stores/modalStore';
import { useUser } from 'zustand-stores/userEnvironmentStore';

import { ViewJobPageRecommendations } from './components/Recommendations/ViewJobPageRecommendations';
import { ViewJobPageSidebar } from './components/Sidebar/ViewJobPageSidebar';

type Props = {
  job: ApiJob;
};

export function ViewJobPage({ job }: Props) {
  const {
    address,
    applyText,
    applyTextTruncated,
    applyUrl,
    applyEmail,
    benefits,
    benefitsTruncated,
    id,
    hasAts,
    startDate,
    endDate,
    functions,
    areasOfFocus,
    requestedAttachments,
    requestedAttachmentsHash,
    isPostedAnonymously,
    name,
    type,
    description,
    descriptionTruncated,
    remoteOptions,
    url,
    hasOrgNameOverride,
    org,
    orgNameOverride,
    applicationDeadline,
    education,
    isContract,
    isFullTime,
    isTemporary,
    professionalLevel,
    salaryCurrency,
    salaryDetails,
    salaryMaximum,
    salaryMinimum,
    salaryPeriod,
    synonym,
    languageProficiency,
    languageProficiencyTruncated,
    firstPublished,
  } = job;

  useSetDocumentTitle(name);

  const { user } = useUser();
  const isOrgOwner = isUserOrgOwner(user, org?.id);

  const { searchContext } = useSearchContextHeader({ currentObjectId: id });

  const listingDetailsSearchResultsEnabled = useMemo(() => {
    if (isOrgOwner && !searchContext) return false;

    return true;
  }, [isOrgOwner, searchContext]);

  useEffect(() => {
    trackListing({
      status: 'published',
      hasAts,
      listingName: name,
      listingId: id,
      orgId: org?.id,
      orgName: org?.name,
      orgType: org?.orgType,
      listingType: 'JOB',
    });
  }, [hasAts, id, name, org?.id, org?.name, org?.orgType]);

  const orgName = getOrgName({ orgNameOverride, isPostedAnonymously, org });

  return (
    <>
      {isOrgOwner && org && (
        <PageToolbar
          centered
          message={getText('Your organization posted this job')}
          tools={
            <ListingViewAdminActionButtons
              listingId={id}
              listingType="JOB"
              orgId={org.id}
              hasAts={hasAts}
              canPost
            />
          }
        />
      )}

      <ListingViewBody>
        <ViewJobPageSidebar
          job={job}
          orgName={orgName}
          listingDetailsSearchResultsEnabled={
            listingDetailsSearchResultsEnabled
          }
          searchContext={searchContext}
          isExpired={false}
        />

        <ListingViewMain>
          {searchContext && (
            <ListingViewHeaderSearchContext searchContext={searchContext} />
          )}

          <ListingViewHeader
            address={address}
            hasAts={hasAts}
            isPostedAnonymously={isPostedAnonymously}
            listingId={id}
            listingName={name}
            listingType={type}
            listingUrl={url}
            listingFirstPublished={firstPublished}
            orgId={org?.id}
            orgName={orgName}
            orgType={org?.orgType}
            orgUrl={hasOrgNameOverride || isPostedAnonymously ? null : org?.url}
            remoteOptions={remoteOptions}
            isExpired={false}
          />

          <ListingViewSection title={getText('Details')} noBorderTop>
            <JobDetailsList
              orgAreasOfFocus={org?.areasOfFocusKeys}
              startDate={startDate}
              endDate={endDate}
              applicationDeadline={applicationDeadline}
              education={education}
              isContract={isContract}
              isFullTime={isFullTime}
              isTemporary={isTemporary}
              professionalLevel={professionalLevel}
              salaryCurrency={salaryCurrency}
              salaryDetails={salaryDetails}
              salaryMaximum={salaryMaximum}
              salaryMinimum={salaryMinimum}
              salaryPeriod={salaryPeriod}
              jobCountry={address.country}
              areasOfFocus={areasOfFocus}
            />
          </ListingViewSection>

          <ListingViewDescriptionSection
            listingName={name}
            image={null}
            description={description}
            descriptionTruncated={descriptionTruncated}
            handleShowEmailModal={(link) => {
              if (!user && isExternalURL(link.href)) {
                showModal('EMAIL_ALERT', {
                  listingFunctions: functions,
                  orgId: org?.id,
                  listingName: name,
                  listingType: 'JOB',
                  synonym,
                });
              }
            }}
            showMoreLinkTitle={getText('Read more about this job')}
            onToggleDetails={(isOpen) => {
              if (!isOpen) return;

              trackClicked('read more about this listing', {
                listing_type: job.type,
                locale: job.locale,
              });
            }}
          />

          <ListingViewBenefitsSection
            benefits={benefits}
            benefitsTruncated={benefitsTruncated}
            onToggleDetails={(isOpen) => {
              if (!isOpen) return;

              trackClicked('read more about benefits', {
                listing_type: job.type,
                locale: job.locale,
              });
            }}
          />

          <ListingViewLanguageProficiencySection
            languageProficiency={languageProficiency}
            languageProficiencyTruncated={languageProficiencyTruncated}
          />

          <ListingViewLocationSection
            listingType="JOB"
            address={address}
            remoteOptions={remoteOptions}
          />

          <ListingViewApplySection
            applyEmail={applyEmail}
            applyText={applyText}
            applyTextTruncated={applyTextTruncated}
            applyUrl={applyUrl}
            functions={functions}
            hasAts={hasAts}
            listingName={name}
            listingType="JOB"
            listingId={id}
            orgType={org?.orgType}
            orgId={org?.id}
            requestedAttachments={requestedAttachments}
            requestedAttachmentsHash={requestedAttachmentsHash}
            synonym={synonym}
          />

          {searchContext ? (
            <PrintHidden>
              <div style={{ marginTop: 70 }}>
                <SearchContextHeader searchContext={searchContext} noDivider />
              </div>
            </PrintHidden>
          ) : null}
        </ListingViewMain>
      </ListingViewBody>

      <ListingViewFooter
        hiddenOnDesktop={Boolean(user && listingDetailsSearchResultsEnabled)}
      >
        <ViewJobPageRecommendations
          jobId={id}
          jobTitle={name}
          synonym={synonym}
          functions={job.functions}
          orgAreasOfFocus={org?.areasOfFocusKeys}
        />
        {!user && (
          <ListingViewSignupCta
            listingUrl={url[CURRENT_LOCALE()]}
            type={type}
          />
        )}
      </ListingViewFooter>

      <ButtonBar>
        <ListingViewActionButtons
          hasAts={hasAts}
          listingId={id}
          listingName={name}
          listingType={type}
          listingUrl={url}
          orgId={org?.id}
          orgName={org?.name}
          orgType={org?.orgType}
          mobile
        />
      </ButtonBar>
    </>
  );
}
